/**
 * Module 00 — Global Shell · Command palette (FE-00-05)
 *
 * ⌘K / Ctrl-K fuzzy search over every (capability-filtered) nav page. Esc
 * closes (G-09), arrow keys move through results, and Enter or click navigates
 * (G-06). Result list is capped at 40 (Blueprint §10, verbatim).
 *
 *
 */

'use client';

import * as React from 'react';
import { useRouter } from 'next/navigation';
import { cn } from '@/lib/utils';
import { Icon } from './icons';
import { filterFlat, type FlatNavItem } from '@/lib/shell/nav';

import { searchDocs, useDocsSearchIndex } from '@/lib/docs/use-docs-search';

const RESULT_CAP = 40;
const DOCS_RESULT_CAP = 12;

export function ShellCommandPalette({
  open,
  close,
  go,
  capabilities,
  orgAdmin = false,
}: {
  open: boolean;
  close: () => void;
  go: (route: string) => void;
  capabilities?: Set<string> | null;
  /** Includes organization-administrator-only pages (see NavItem.orgAdminOnly). */
  orgAdmin?: boolean;
}) {
  const [q, setQ] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const activeResultRef = React.useRef<HTMLButtonElement>(null);
  const router = useRouter();
  const docsIndex = useDocsSearchIndex(open);

  const index = React.useMemo<FlatNavItem[]>(
    () => filterFlat(capabilities, { orgAdmin }),
    [capabilities, orgAdmin],
  );

  React.useEffect(() => {
    if (open) {
      setQ('');
      setActiveIndex(0);
      const t = setTimeout(() => inputRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
  }, [open]);

  const needle = q.toLowerCase();
  const results = index
    .filter((it) => `${it.label} ${it.group}`.toLowerCase().includes(needle))
    .slice(0, RESULT_CAP);

  const docsResults = searchDocs(docsIndex, q, DOCS_RESULT_CAP);
  const resultCount = results.length + docsResults.length;

  React.useEffect(() => {
    setActiveIndex((current) => {
      if (resultCount === 0) return -1;
      if (current < 0 || current >= resultCount) return 0;
      return current;
    });
  }, [resultCount]);

  React.useEffect(() => {
    activeResultRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  if (!open) return null;

  const navigate = (route: string) => {
    go(route);
    close();
  };

  const openDoc = (href: string) => {
    router.push(href);
    close();
  };

  const activateResult = (index: number) => {
    if (index < results.length) {
      const result = results[index];
      if (result) navigate(result.route);
      return;
    }

    const doc = docsResults[index - results.length];
    if (doc) openDoc(doc.href);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (resultCount > 0) {
        setActiveIndex((current) => (current + 1 + resultCount) % resultCount);
      }
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (resultCount > 0) {
        setActiveIndex((current) => (current - 1 + resultCount) % resultCount);
      }
      return;
    }

    if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      activateResult(activeIndex);
    }
  };

  return (
    <div
      data-testid="command-palette"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onClick={close}
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/50 pt-[12vh] backdrop-blur-sm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[580px] max-w-[90vw] overflow-hidden rounded-xl border border-cs-border bg-cs-surface-card shadow-2xl"
      >
        <div className="flex items-center gap-3 border-b border-cs-border px-4 py-3">
          <Icon name="search" size={18} className="text-cs-text-muted" />
          <input
              ref={inputRef}
              value={q}
              data-testid="palette-input"
              role="combobox"
              aria-autocomplete="list"
              aria-controls="command-palette-results"
              aria-expanded="true"
              aria-activedescendant={
                activeIndex >= 0 ? `command-palette-option-${activeIndex}` : undefined
              }
              onChange={(e) => {
                setQ(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleInputKeyDown}
              placeholder="Search agents, entities, tools, pages…"
              className="flex-1 bg-transparent text-[15px] text-cs-text-primary outline-none placeholder:text-cs-text-disabled"
            />
          <kbd className="rounded border border-cs-border px-1.5 py-0.5 text-[10px] text-cs-text-muted">
            Esc
          </kbd>
        </div>
        <div
          id="command-palette-results"
          role="listbox"
          aria-label="Search results"
          className="max-h-[380px] overflow-y-auto p-2"
        >
          {results.length === 0 && docsResults.length === 0 ? (
            <div className="px-7 py-7 text-center text-[13.5px] text-cs-text-muted">
              No results for &ldquo;{q}&rdquo;
            </div>
          ) : (
            results.map((it, index) => (
              <button key={`${it.group}-${it.route}`}
                  ref={index === activeIndex ? activeResultRef : undefined}
                  id={`command-palette-option-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  type="button"
                  data-testid={`palette-result-${it.route}`}
                  onClick={() => navigate(it.route)}
                  onMouseEnter={() => setActiveIndex(index)}
                  onFocus={() => setActiveIndex(index)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                    index === activeIndex && 'bg-cs-surface-hover',
                  )}
                >
                  <div className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg bg-cs-surface-raised text-cs-text-secondary">
                    <Icon name={it.icon} size={16} />
                  </div>
                  <span className="text-[13.5px] font-semibold text-cs-text-primary">
                    {it.label}
                  </span>
                  <span className="ml-auto text-[11.5px] text-cs-text-muted">{it.group}</span>
                </button>
            ))
          )}

          {docsResults.length > 0 ? (
            <div className="mt-1">
              <p className="px-3 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-cs-text-muted">
                Documentation
              </p>
              {docsResults.map((doc, index) => {
                const optionIndex = results.length + index;
                return (
                  <button
                    key={doc.href}
                    ref={optionIndex === activeIndex ? activeResultRef : undefined}
                    id={`command-palette-option-${optionIndex}`}
                    role="option"
                    aria-selected={optionIndex === activeIndex}
                    type="button"
                    data-testid={`palette-doc-${doc.href}`}
                    onClick={() => openDoc(doc.href)}
                    onMouseEnter={() => setActiveIndex(optionIndex)}
                    onFocus={() => setActiveIndex(optionIndex)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                      optionIndex === activeIndex && 'bg-cs-surface-hover',
                    )}
                  >
                    <div className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg bg-cs-surface-raised text-cs-text-secondary">
                      <Icon name="book" size={16} />
                    </div>
                    <span className="truncate text-[13.5px] font-semibold text-cs-text-primary">
                      {doc.label}
                    </span>
                    <span className="ml-auto shrink-0 pl-3 text-[11.5px] text-cs-text-muted">
                      {doc.context}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
